/**
 * Group detail — sidebar list + tab panel switching (Mode B chrome).
 */

const SIDEBAR_ICON_COLORS = ["blue", "green", "purple", "red", "orange", "yellow"];

function sidebarIconColor(groupKey, idx) {
  const n = SIDEBAR_ICON_COLORS.length;
  const i = Number.isFinite(idx) ? idx % n : 0;
  return SIDEBAR_ICON_COLORS[i];
}

function sidebarStatus(slot) {
  const total = Number(slot?.serverCounts?.total ?? 0);
  const online = Number(slot?.serverCounts?.online ?? 0);
  if (total > 0 && online === 0) return { key: "critical", label: "Critical", cls: "offline" };
  if (online > 0) return { key: "active", label: "Active", cls: "online" };
  if (total === 0) return { key: "inactive", label: "Inactive", cls: "neutral" };
  return { key: "inactive", label: "Inactive", cls: "partial" };
}

/**
 * @param {object} ctx
 * @returns {string}
 */
export function buildAllDevicesSidebarCardHtml(ctx) {
  const {
    escapeHtml,
    devices = [],
    allDevicesActive = false,
    devicePresenceStatus,
    devicePresenceBadgeText,
  } = ctx;
  const list = Array.isArray(devices) ? devices : [];
  if (!list.length) {
    return `<div class="grp-sidebar-all-devices-list">
      <p class="muted grp-sidebar-empty">No boards yet.</p>
      <a class="grp-sidebar-all-more" href="#/group/_all">Browse all boards →</a>
    </div>`;
  }
  const sidebarDeviceCap = 24;
  const cards = list.slice(0, sidebarDeviceCap).map((d) => {
    const did = String((d && d.device_id) || "").trim();
    if (!did) return "";
    const name = String((d && d.display_label) || did).trim() || did;
    const fw = d && d.fw != null && String(d.fw).trim() ? `v${String(d.fw).trim()}` : "";
    const zone = d && d.zone != null && String(d.zone).trim() ? String(d.zone).trim() : "";
    const meta = [fw, zone].filter(Boolean).join(" · ") || "—";
    const presence = typeof devicePresenceStatus === "function"
      ? devicePresenceStatus(d)
      : { cls: "neutral" };
    const statusLabel = typeof devicePresenceBadgeText === "function"
      ? devicePresenceBadgeText(presence)
      : (presence.cls === "online" ? "Online" : "Offline");
    const initial = escapeHtml(name.charAt(0).toUpperCase() || "D");
    return `<div class="grp-sidebar-item grp-sidebar-item--device" data-grp-sidebar-device="${escapeHtml(did)}">
      <a class="grp-sidebar-card croc-cc-zone" href="#/devices/${encodeURIComponent(did)}">
        <span class="croc-cc-zone__icon" aria-hidden="true">${initial}</span>
        <span class="croc-cc-zone__body">
          <span class="grp-sidebar-card__row">
            <strong class="croc-cc-zone__name" title="${escapeHtml(did)}">${escapeHtml(name)}</strong>
            <span class="badge ${escapeHtml(presence.cls || "neutral")} grp-sidebar-card__badge">${escapeHtml(statusLabel)}</span>
          </span>
          <span class="croc-cc-zone__meta">${escapeHtml(meta)}</span>
        </span>
      </a>
    </div>`;
  }).filter(Boolean).join("");
  const moreLabel = list.length > sidebarDeviceCap
    ? `View all ${list.length} boards →`
    : "Open all boards →";
  return `<div class="grp-sidebar-all-devices-list">${cards}<a class="grp-sidebar-all-more${allDevicesActive ? " grp-sidebar-all-more--active" : ""}" href="#/group/_all">${moreLabel}</a></div>`;
}

/**
 * @param {ParentNode} root
 * @param {object} ctx
 */
export function paintGroupSidebarAllDevicesCard(root, ctx) {
  const el = root && root.querySelector("#grpSidebarAllDevices");
  if (!el) return;
  el.innerHTML = buildAllDevicesSidebarCardHtml(ctx);
}

/**
 * @param {Array<object>} slots
 * @param {object} ctx
 * @returns {string}
 */
export function buildGroupSidebarListHtml(slots, ctx) {
  const {
    escapeHtml,
    displayGroupName,
    resolveOverviewGroupCardTitle,
    meta,
    canonicalGroupKey,
    activeGroupKey,
    activeTenantOwner,
    showRevokeCheckbox = false,
  } = ctx;
  const list = Array.isArray(slots) ? slots : [];
  if (!list.length) {
    return '<p class="muted grp-sidebar-empty">No groups yet.</p>';
  }
  const activeGk = canonicalGroupKey(activeGroupKey || "");
  const activeOwner = String(activeTenantOwner || "").trim();
  const groupRows = list
    .map((slot, idx) => {
      const gk = String(slot.groupKey || "");
      const tenant = String(slot.tenantOwner || "").trim();
      const title = resolveOverviewGroupCardTitle(
        slot,
        (meta && meta[slot.metaKey]) || {},
        displayGroupName,
      );
      const total = Number(slot.serverCounts?.total ?? 0);
      const online = Number(slot.serverCounts?.online ?? 0);
      const st = sidebarStatus(slot);
      const isCurrent = activeGk === canonicalGroupKey(gk) && activeOwner === tenant;
      const displayKey = String(slot.groupDisplaySample || gk || title).trim() || gk;
      const href = `#/group/${encodeURIComponent(displayKey)}${tenant ? `?owner=${encodeURIComponent(tenant)}` : ""}`;
      const color = sidebarIconColor(gk, idx);
      const initial = escapeHtml(String(title || "?").trim().charAt(0).toUpperCase() || "G");
      const zoneMod = color === "blue" ? "" : ` croc-cc-zone--${color}`;
      const hasShare = !!slot.hasGroupShare;
      const granteeCount = Number(slot.granteeCount ?? 0);
      const shareBadge = hasShare
        ? `<span class="badge online grp-sidebar-share-badge" title="Group shared">Shared · ${granteeCount || "—"}</span>`
        : `<span class="badge neutral grp-sidebar-share-badge" title="No group share">Private</span>`;
      const hubOwner = escapeHtml(String(slot.hubOwnerAdmin || tenant || ""));
      const hubGroup = escapeHtml(String(slot.hubGroupDisplay || displayKey || gk));
      const chk = showRevokeCheckbox && hasShare
        ? `<label class="grp-sidebar-sel" title="Select for bulk revoke">
            <input type="checkbox" class="js-grp-sidebar-revoke-sel"
              data-owner="${hubOwner}" data-group="${hubGroup}"
              aria-label="Select ${escapeHtml(title)} for share revoke" />
          </label>`
        : `<span class="grp-sidebar-sel grp-sidebar-sel--spacer" aria-hidden="true"></span>`;
      return `<div class="grp-sidebar-item${isCurrent ? " grp-sidebar-item--active" : ""}"
        data-grp-sidebar-status="${st.key}"
        data-grp-sidebar-shared="${hasShare ? "1" : "0"}"
        data-grp-sidebar-title="${escapeHtml(String(title).toLowerCase())}">
        ${chk}
        <a class="grp-sidebar-card croc-cc-zone${zoneMod}${isCurrent ? " grp-sidebar-card--active" : ""}" href="${href}">
          <span class="croc-cc-zone__icon" aria-hidden="true">${initial}</span>
          <span class="croc-cc-zone__body">
            <span class="grp-sidebar-card__row">
              <strong class="croc-cc-zone__name">${escapeHtml(title)}</strong>
              <span class="badge ${st.cls} grp-sidebar-card__badge">${st.label}</span>
            </span>
            <span class="croc-cc-zone__meta">${total} device${total === 1 ? "" : "s"} · ${online} online</span>
            <span class="grp-sidebar-card__share">${shareBadge}</span>
          </span>
        </a>
      </div>`;
    })
    .join("");
  return groupRows;
}

/**
 * @param {ParentNode} root
 * @param {object} [opts]
 */
export function wireGroupDetailTabs(root, opts = {}) {
  if (!root) return () => {};
  const tabs = root.querySelectorAll("[data-grp-tab]");
  const panels = root.querySelectorAll("[data-grp-panel]");
  const sidebarFilters = root.querySelectorAll("[data-grp-sidebar-filter]");
  const shareFilters = root.querySelectorAll("[data-grp-sidebar-share-filter]");
  const sidebarSearch = root.querySelector("#grpSidebarSearch");
  const sidebarList = root.querySelector("#grpSidebarList");
  const onPanelShow = typeof opts.onPanelShow === "function" ? opts.onPanelShow : null;

  const showPanel = (name) => {
    const id = String(name || "overview");
    tabs.forEach((btn) => {
      const on = btn.getAttribute("data-grp-tab") === id;
      btn.classList.toggle("grp-mode-b__tab--active", on);
      btn.setAttribute("aria-selected", on ? "true" : "false");
    });
    panels.forEach((panel) => {
      const on = panel.getAttribute("data-grp-panel") === id;
      panel.hidden = !on;
      panel.classList.toggle("grp-mode-b__panel--active", on);
    });
    if (onPanelShow) onPanelShow(id);
  };

  const activeStatusFilter = () => {
    const active = root.querySelector(".croc-cc-pill--on[data-grp-sidebar-filter]");
    return active ? active.getAttribute("data-grp-sidebar-filter") : "all";
  };

  const activeShareFilter = () => {
    const active = root.querySelector(".croc-cc-pill--on[data-grp-sidebar-share-filter]");
    return active ? active.getAttribute("data-grp-sidebar-share-filter") : "all";
  };

  const applySidebarFilter = () => {
    const statusFlt = activeStatusFilter() || "all";
    const shareFlt = activeShareFilter() || "all";
    sidebarFilters.forEach((btn) => {
      btn.classList.toggle("croc-cc-pill--on", btn.getAttribute("data-grp-sidebar-filter") === statusFlt);
    });
    shareFilters.forEach((btn) => {
      btn.classList.toggle("croc-cc-pill--on", btn.getAttribute("data-grp-sidebar-share-filter") === shareFlt);
    });
    if (!sidebarList) return;
    const q = sidebarSearch ? String(sidebarSearch.value || "").trim().toLowerCase() : "";
    sidebarList.querySelectorAll(".grp-sidebar-item").forEach((item) => {
      const status = item.getAttribute("data-grp-sidebar-status") || "";
      const shared = item.getAttribute("data-grp-sidebar-shared") || "0";
      const title = item.getAttribute("data-grp-sidebar-title") || "";
      const statusOk = statusFlt === "all" || status === statusFlt;
      const shareOk = shareFlt === "all"
        || (shareFlt === "shared" && shared === "1")
        || (shareFlt === "unshared" && shared === "0");
      const searchOk = !q || title.includes(q) || (item.textContent || "").toLowerCase().includes(q);
      item.style.display = statusOk && shareOk && searchOk ? "" : "none";
    });
  };

  const onTabClick = (ev) => {
    const btn = ev.target.closest("[data-grp-tab]");
    if (!btn || !root.contains(btn)) return;
    ev.preventDefault();
    showPanel(btn.getAttribute("data-grp-tab"));
  };
  const onJumpClick = (ev) => {
    const btn = ev.target.closest("[data-grp-tab-jump]");
    if (!btn || !root.contains(btn)) return;
    ev.preventDefault();
    showPanel(btn.getAttribute("data-grp-tab-jump"));
  };
  const onStatusFilterClick = (ev) => {
    const btn = ev.target.closest("[data-grp-sidebar-filter]");
    if (!btn || !root.contains(btn)) return;
    sidebarFilters.forEach((b) => {
      b.classList.toggle("croc-cc-pill--on", b === btn);
    });
    applySidebarFilter();
  };
  const onShareFilterClick = (ev) => {
    const btn = ev.target.closest("[data-grp-sidebar-share-filter]");
    if (!btn || !root.contains(btn)) return;
    shareFilters.forEach((b) => {
      b.classList.toggle("croc-cc-pill--on", b === btn);
    });
    applySidebarFilter();
  };
  const onSearch = () => applySidebarFilter();
  const onCheckboxClick = (ev) => {
    if (ev.target && ev.target.classList && ev.target.classList.contains("js-grp-sidebar-revoke-sel")) {
      ev.stopPropagation();
    }
  };

  root.addEventListener("click", onTabClick);
  root.addEventListener("click", onJumpClick);
  root.addEventListener("click", onStatusFilterClick);
  root.addEventListener("click", onShareFilterClick);
  root.addEventListener("click", onCheckboxClick);
  if (sidebarSearch) sidebarSearch.addEventListener("input", onSearch);
  const initial = String(opts.initialPanel || "overview");
  showPanel(initial);
  applySidebarFilter();

  return () => {
    root.removeEventListener("click", onTabClick);
    root.removeEventListener("click", onJumpClick);
    root.removeEventListener("click", onStatusFilterClick);
    root.removeEventListener("click", onShareFilterClick);
    root.removeEventListener("click", onCheckboxClick);
    if (sidebarSearch) sidebarSearch.removeEventListener("input", onSearch);
  };
}

/**
 * Wire the Create Group modal on the group detail page.
 * @param {ParentNode} root
 * @param {object} ctx
 * @returns {() => void}
 */
export function wireCreateGroupModal(root, ctx) {
  const {
    api,
    toast,
    escapeHtml,
    canonicalGroupKey,
    fetchFleetDevicesList,
    buildPatchDeviceProfilePath,
    isRouteCurrent,
    routeSeq,
  } = ctx;

  const btn = root.querySelector("#grpCreateGroupBtn");
  const emptyBtn = root.querySelector("#grpEmptyCreateBtn");
  const modal = document.getElementById("createGroupModal");
  if ((!btn && !emptyBtn) || !modal) return () => {};

  let devices = [];
  let submitting = false;

  const openBtn = modal.querySelector("#createGroupModalClose");
  const cancelBtn = modal.querySelector("#createGroupModalCancel");
  const submitBtn = modal.querySelector("#createGroupModalSubmit");
  const nameInput = modal.querySelector("#createGroupName");
  const listEl = modal.querySelector("#createGroupDevList");

  const close = () => {
    modal.hidden = true;
    modal.setAttribute("aria-hidden", "true");
    modal.classList.remove("is-open");
    if (nameInput) nameInput.value = "";
  };

  const open = () => {
    modal.hidden = false;
    modal.removeAttribute("aria-hidden");
    modal.classList.add("is-open");
    loadDevices();
    if (nameInput) nameInput.focus();
  };

  const renderDevices = () => {
    if (!listEl) return;
    if (devices.length === 0) {
      listEl.innerHTML = `<p class="muted">No boards available.</p>`;
      return;
    }
    listEl.innerHTML = devices.map((d) => {
      const did = escapeHtml(String(d.device_id || ""));
      const name = escapeHtml(String(d.display_label || d.device_id || "Board"));
      const group = escapeHtml(String(d.notification_group || "").trim());
      const meta = group ? `Current group: ${group}` : "Ungrouped";
      return `
        <label class="create-group-dev-item" data-did="${did}">
          <input type="checkbox" class="create-group-dev-check" data-did="${did}" />
          <span class="create-group-dev-item__label">
            <span class="create-group-dev-item__name">${name}</span>
            <span class="create-group-dev-item__meta">${meta}</span>
          </span>
        </label>`;
    }).join("");
  };

  const loadDevices = async () => {
    if (!listEl) return;
    listEl.innerHTML = `<p class="muted">Loading devices…</p>`;
    try {
      // KILL-MERGE: create-group picker = one page; full membership via All devices Load more.
      const res = await fetchFleetDevicesList({ maxPages: 1 });
      devices = (res && res.items) || [];
      if (!isRouteCurrent(routeSeq)) return;
      renderDevices();
      updateSubmit();
    } catch (e) {
      if (!isRouteCurrent(routeSeq)) return;
      toast(e.message || "Failed to load devices", "err");
      listEl.innerHTML = `<p class="badge offline">Failed to load devices</p>`;
    }
  };

  const updateSubmit = () => {
    if (!submitBtn || !nameInput) return;
    const name = String(nameInput.value || "").trim();
    const checked = modal.querySelectorAll(".create-group-dev-check:checked").length;
    submitBtn.disabled = !name || checked === 0 || submitting;
  };

  const submit = async () => {
    if (!nameInput || submitting) return;
    const rawName = String(nameInput.value || "").trim();
    const groupName = canonicalGroupKey(rawName);
    if (!groupName) {
      toast("Please enter a group name", "warn");
      return;
    }
    const selected = [...modal.querySelectorAll(".create-group-dev-check:checked")]
      .map((cb) => String(cb.dataset.did || "").trim())
      .filter(Boolean);
    if (selected.length === 0) {
      toast("Please select at least one device", "warn");
      return;
    }

    submitting = true;
    updateSubmit();
    let ok = 0;
    let failed = 0;
    for (const did of selected) {
      try {
        await api(buildPatchDeviceProfilePath(did), {
          method: "PATCH",
          body: { notification_group: groupName },
        });
        ok++;
      } catch (_e) {
        failed++;
      }
    }

    submitting = false;
    updateSubmit();
    if (!isRouteCurrent(routeSeq)) return;

    if (failed === 0) {
      toast(`Group "${groupName}" created with ${ok} devices`, "ok");
      close();
      location.hash = `#/group/${encodeURIComponent(groupName)}`;
    } else {
      toast(`${ok} updated, ${failed} failed`, "err");
    }
  };

  const onModalClick = (ev) => {
    if (ev.target === modal) close();
  };
  const onModalChange = (ev) => {
    if (ev.target && ev.target.classList && ev.target.classList.contains("create-group-dev-check")) updateSubmit();
  };

  if (btn) btn.addEventListener("click", open);
  if (emptyBtn) emptyBtn.addEventListener("click", open);
  if (openBtn) openBtn.addEventListener("click", close);
  if (cancelBtn) cancelBtn.addEventListener("click", close);
  if (nameInput) nameInput.addEventListener("input", updateSubmit);
  if (submitBtn) submitBtn.addEventListener("click", submit);
  modal.addEventListener("click", onModalClick);
  modal.addEventListener("change", onModalChange);

  return () => {
    if (btn) btn.removeEventListener("click", open);
    if (emptyBtn) emptyBtn.removeEventListener("click", open);
    if (openBtn) openBtn.removeEventListener("click", close);
    if (cancelBtn) cancelBtn.removeEventListener("click", close);
    if (nameInput) nameInput.removeEventListener("input", updateSubmit);
    if (submitBtn) submitBtn.removeEventListener("click", submit);
    modal.removeEventListener("click", onModalClick);
    modal.removeEventListener("change", onModalChange);
  };
}
